import { compose } from 'ramda';
import makeButtonHandler from '../formEventHandlers/buttonHandler';
import { handleHyperlinkBoxClick } from '../view/hyperlinkBox.helpers';
import widgetManager from '../widgetManager';
import xmlActionExecutor from '../xmlActionExecutor';
import {
    VIEW_EVENT_BUTTON,
    VIEW_EVENT_FIELDCHANGE,
    VIEW_EVENT_FILEDROP,
    VIEW_EVENT_HYPERLINK_BOX_CLICK,
    VIEW_EVENT_INSERT,
    VIEW_EVENT_MENUCLICK,
    VIEW_EVENT_MULTISELECT,
    VIEW_EVENT_PEOPLE_ADDRESS,
    VIEW_EVENT_PEOPLE_CHANGE,
    VIEW_EVENT_PEOPLE_SEARCH,
    VIEW_EVENT_CUSTOM_SEARCH_CHANGE,
    VIEW_EVENT_SHOWMENU,
    VIEW_EVENT_SHOWMENUHANDLE,
} from './viewActionHandler.constants';
import fieldChangeActionHandler from './viewActionHandler.fieldChange';
import insertRowActionHandler from './viewActionHandler.insertRow';
import multiSelectActionHandler from './viewActionHandler.multiSelect';
import peoplePickerActionHandler from './viewActionHandler.peoplePicker';

const LOADER_DELAY_MS = 250;

const viewActionHandler = (
    qfsAccess,
    shpAccess,
    hostWebUrl,
    validationInst,
    renderView,
    runRuleSet,
    template,
    viewManager,
    showLoader,
    renderTarget,
    dataConnections,
    dataSources,
    unhandledError,
    fileDropHandlerInst,
    peopleSearcher,
) => {
    const useLoader = async (action) => {
        if (showLoader) {
            await showLoader(action, {
                delay: LOADER_DELAY_MS,
                status: 'Processing...',
            });
        } else {
            await action;
        }
    };

    const getNodeForNodeId = (nodeId) => dataSources.getNodeById(nodeId);

    const { handleFieldChangeAsync } = fieldChangeActionHandler(
        validationInst,
        renderView,
        getNodeForNodeId,
    );
    const buttonHandler = makeButtonHandler({
        template,
        viewManager,
        getNodeForNodeId,
        runRuleSetAsync: runRuleSet,
        useLoader,
        renderViewAsync: renderView,
        renderTarget,
        dataConnections,
        dataSources,
        shpAccess,
    });
    const { handleInsertClickAsync } = insertRowActionHandler(
        getNodeForNodeId,
        viewManager,
        template,
        renderView,
    );
    const { showWidgetMenu, showDropdownWidget, processMenuItemClick } =
        widgetManager(template, viewManager, {
            getNodeForNodeId,
        });
    const xmlActionExecutorInst = xmlActionExecutor(dataSources);
    const handleMenuItemClickAsync = async (eventInfo) => {
        const action = processMenuItemClick(eventInfo);

        await xmlActionExecutorInst
            .performXmlToEditActionAsync(
                action.target,
                action.action,
                action.xmlToEdit,
            )
            .catch(unhandledError)
            .then(renderView);
    };
    const {
        createPeoplePickerElement,
        handlePeopleAddressSearch,
        handlePeopleUserCheck,
        searchUserForCustomPicker,
    } = peoplePickerActionHandler(
        qfsAccess,
        hostWebUrl,
        dataSources,
        renderView,
        peopleSearcher,
    );
    const { handleMultiSelectClick } = multiSelectActionHandler(
        getNodeForNodeId,
        template,
        renderView,
    );

    const handlerMap = {
        [VIEW_EVENT_FIELDCHANGE]: compose(useLoader, handleFieldChangeAsync),
        [VIEW_EVENT_BUTTON]: buttonHandler,
        [VIEW_EVENT_INSERT]: compose(useLoader, handleInsertClickAsync),
        [VIEW_EVENT_SHOWMENU]: showWidgetMenu,
        [VIEW_EVENT_SHOWMENUHANDLE]: showDropdownWidget,
        [VIEW_EVENT_MENUCLICK]: handleMenuItemClickAsync,
        [VIEW_EVENT_PEOPLE_ADDRESS]: handlePeopleAddressSearch,
        [VIEW_EVENT_PEOPLE_SEARCH]: handlePeopleUserCheck,
        [VIEW_EVENT_PEOPLE_CHANGE]: createPeoplePickerElement,
        [VIEW_EVENT_CUSTOM_SEARCH_CHANGE]: searchUserForCustomPicker,
        [VIEW_EVENT_FILEDROP]: fileDropHandlerInst.fileDropAsync,
        [VIEW_EVENT_MULTISELECT]: handleMultiSelectClick,
        [VIEW_EVENT_HYPERLINK_BOX_CLICK]: handleHyperlinkBoxClick(
            getNodeForNodeId,
            renderView,
        ),
    };

    async function runViewEvent(eventInfo) {
        const eventType = eventInfo.type;
        const handler = handlerMap[eventType];

        if (!handler) {
            throw new Error(`Unsupported view event: ${eventType}`);
        }

        await handler(eventInfo);
    }

    return {
        runViewEvent,
    };
};

export default viewActionHandler;
